import React, { useEffect, useState } from "react";

const PLAY_STORE_PACKAGE = "com.wearess";
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE}`;

// Android Chrome only — checks the app declared in manifest.json's
// related_applications (see public/manifest.json) against apps actually
// installed on the device. Not available on iOS Safari or desktop browsers,
// so this resolves to "unknown" there and the banner falls back to always
// showing (nothing to check against, and it's harmless to offer the link).
declare global {
    interface Navigator {
        getInstalledRelatedApps?: () => Promise<Array<{ id?: string; platform?: string }>>;
    }
}

function detectPlatform(): "android" | "ios" | "other" {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "android";
    if (/iphone|ipad|ipod/i.test(ua)) return "ios";
    return "other";
}

// Default behavior (forceShow=false/undefined) is mobile-Android-only, since
// desktop visitors aren't the audience for a mobile app install nudge. The
// admin's per-page "Install Banner" toggle (see HomeContentPage.tsx) sets
// forceShow=true to override that and show on every device, since an admin
// deliberately turning this on for a page is an explicit override of the
// default smart behavior.
//
// No dismiss/close control by design — this is meant to stay visible on the
// page permanently wherever the admin has placed it (unlike the earlier
// floating version, which needed a close button since it covered content).
const AppInstallBanner: React.FC<{ forceShow?: boolean }> = ({ forceShow }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (forceShow) {
            setVisible(true);
            return;
        }

        const platform = detectPlatform();
        // iOS: no reliable "already installed?" signal is available to page
        // JS the way Android's getInstalledRelatedApps is — showing an
        // unverified banner here would risk nagging someone who already has
        // the app, which is exactly what was asked NOT to do. Holding off
        // until there's an App Store id to wire up Safari's own native smart
        // app banner instead (that one asks Safari, not us, so it's exact).
        if (platform !== "android") return;

        if (!navigator.getInstalledRelatedApps) {
            // Not Chrome (or too old) — can't check, but Android + Play Store
            // link is still valid, so default to showing it.
            setVisible(true);
            return;
        }

        navigator.getInstalledRelatedApps()
            .then((apps) => {
                const alreadyInstalled = apps.some((a) => a.platform === "play" && a.id === PLAY_STORE_PACKAGE);
                setVisible(!alreadyInstalled);
            })
            .catch(() => setVisible(true));
    }, [forceShow]);

    if (!visible) return null;

    return (
        // In normal document flow (not position:fixed) — sits wherever
        // HomeContentView places it (just above the footer), scaling along
        // with the rest of the page's zoomed content instead of floating
        // over the viewport. Full width, no dismiss control.
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "16px 24px",
                margin: "16px 0",
                width: "100%",
                boxSizing: "border-box",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                flexWrap: "wrap",
            }}
        >
            <img src="/logo192.png" alt="Sagar Samaj" style={{ width: "48px", height: "48px", borderRadius: "10px", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: "160px" }}>
                <div style={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>Sagar Samaj app</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>Install the app, or rate us on Google Play</div>
            </div>
            <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    // #22c55e (green-500) against white text was only a 2.27:1
                    // contrast ratio — WCAG AA needs 4.5:1 for this font size.
                    background: "#15803d",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600,
                    padding: "10px 22px",
                    borderRadius: "8px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                }}
            >
                Install
            </a>
            <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    background: "#264164",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600,
                    padding: "10px 22px",
                    borderRadius: "8px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                }}
            >
                Rate us
            </a>
        </div>
    );
};

export default AppInstallBanner;
